import {
    useRef,
    useLayoutEffect,
    useContext,
    useEffect,
    useState,
    useCallback,
    useMemo,
    memo,
} from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5map from '@amcharts/amcharts5/map';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

import { applyDefault } from './lib/applyDefault';
import { applyHeat, removeHeat } from './lib/applyHeat';
import { applySectorScore } from './lib/applySectorScore';
import { rotateToCountry } from './lib/helpers';

import useViewportChange from '@helpers/useViewportChange';
import { getHubAccess } from '@helpers/utils';

import { GlobeContext } from '@providers/Globe';
import { CountriesContext } from '@providers/Countries';

const Globe = () => {
    const { isMobile } = useViewportChange();

    const root = useRef<any>(null);
    const backgroundSeries = useRef<any>(null);
    const selectedCountryPolygon = useRef<any>(null);
    const mainChart = useRef<any>(null);

    const [polygonSeries, setPolygonSeries] = useState(null);
    const [polygonSeriesHeatMap, setPolygonSeriesHeatMap] = useState(null);
    const [appliedRisk, setAppliedRisk] = useState<any>(null);
    const [initiated, setInitiated] = useState<any>(false);

    const {
        globePosition,
        setSelectedCountry,
        globeRisk,
        globeData,
        setGlobeData,
        setHoveredCountry,
    } = useContext(GlobeContext);
    const { countryDetails, countryList } = useContext(CountriesContext);

    const handleSelectCountry = useCallback(
        countryCode => {
            if (countryCode && countryList && countryList.length > 0) {
                const selectCountryDetails = countryList.find(country => country.countryCode === countryCode)
                if (selectCountryDetails) {
                    if (getHubAccess().find((hub: any) => hub.hubId === selectCountryDetails?.id)) {
                        setSelectedCountry && setSelectedCountry(countryCode);
                    }
                }
            }
        },
        [setSelectedCountry, countryList],
    );

    useEffect(() => {
        am5.addLicense('AM5M326788873');
        am5.addLicense('AM5C326788873');

        if (countryDetails?.length > 0) {
            const newGlobeData = countryDetails.map((item: any) => ({
                ...item,
                itemId: item.id,
                id: item.code,
            }));
            setGlobeData && setGlobeData(newGlobeData);
        }
    }, [setGlobeData, countryDetails]);

    useEffect(() => {
        // @ts-ignore
        if (globeData?.length > 0) {
            if (root.current) {
                root.current.dispose();
                setAppliedRisk(null);
            }

            // Create root element
            root.current = am5.Root.new('globe');

            // Set themes
            root.current.setThemes([am5themes_Animated.new(root.current)]);

            // Create the map chart
            mainChart.current = root.current.container.children.push(
                am5map.MapChart.new(root.current, {
                    panX: "rotateX",
                    panY: "rotateY",
                    wheelY: "zoom",
                    projection: am5map.geoOrthographic(),
                }),
            );

            //START Zoom Buttons
            const zoomControl = mainChart.current.set("zoomControl", am5map.ZoomControl.new(root.current, {}));

            const homeButton = zoomControl.children.moveValue(am5.Button.new(root.current, {
                paddingTop: 10,
                paddingBottom: 10,
                cursorOverStyle: "pointer",
                icon:
                    am5.Graphics.new(root.current, {
                        svgPath: "M16,8 L14,8 L14,16 L10,16 L10,10 L6,10 L6,16 L2,16 L2,8 L0,8 L8,0 L16,8 Z M16,8",
                        fill: am5.color(0xffffff)
                    })
            }), 0)
            homeButton.get("background").setAll({
                fill: am5.color(0x003b5c),
            });
            homeButton.get("background").states.create("hover", {}).setAll({
                fill: am5.color(0x8795a8)
            });

            homeButton.events.on("click", function () {
                mainChart.current.goHome();
            })

            //END Zoom Buttons

            // @ts-ignore
            mainChart.current.panBehavior = 'rotateLongLat';
            mainChart.current.deltaLatitude = 20;

            applyDefault(
                root.current,
                polygonSeries,
                backgroundSeries.current,
                mainChart.current,
                selectedCountryPolygon.current,
                setPolygonSeries,
                handleSelectCountry,
            );

            setTimeout(() => {
                setInitiated(true);
            }, 100);
        }

        return () => {
            if (root.current) {
                root.current.dispose();
            }
        };


        // eslint-disable-next-line
    }, [globeData]);

    useEffect(() => {
        if (
            root.current &&
            mainChart.current &&
            initiated &&
            (appliedRisk !== globeRisk)
        ) {
            removeHeat(mainChart.current, polygonSeriesHeatMap);
            if (typeof globeRisk === "string") {
                switch (globeRisk) {
                    case 'glriRank':
                    case 'greenRank':
                    case 'ability':
                    case 'knowledge':
                    case 'skills':
                    case 'workActivity':
                    case 'occupationPerc':
                        applyHeat(
                            root.current,
                            mainChart.current,
                            globeData,
                            globeRisk,
                            setPolygonSeriesHeatMap,
                            selectedCountryPolygon.current,
                            handleSelectCountry,
                            setHoveredCountry,
                        );
                        break;
                    default:
                        break;
                }
            } else {
                let sectorScoreData = globeRisk?.data.map(e => ({
                    name: e.countryName,
                    id: e.countryCode,
                    score: e.score
                }))
                applySectorScore(
                    root.current,
                    mainChart.current,
                    sectorScoreData,
                    setPolygonSeriesHeatMap,
                    selectedCountryPolygon.current,
                    handleSelectCountry,
                    setHoveredCountry,
                )
            }
            setAppliedRisk(globeRisk);
        }
    }, [
        initiated,
        globeRisk,
        globeData,
        polygonSeries,
        appliedRisk,
        polygonSeriesHeatMap,
        handleSelectCountry,
        setHoveredCountry,
        setPolygonSeriesHeatMap,
        mainChart,
        root
    ]);

    useLayoutEffect(() => {
        if (globePosition && mainChart.current) {
            rotateToCountry(
                mainChart.current,
                polygonSeries,
                polygonSeriesHeatMap,
                globePosition,
            );
        }
    }, [globePosition, polygonSeries, polygonSeriesHeatMap]);

    const globeStyles = useMemo(() => {
        const width = isMobile ? 'calc(100vw - 224px)' : '100%';
        const height = isMobile ? 'calc(100vh - 80px)' : 'calc(100vh - 214px)';

        return {
            width,
            height,
        };


    }, [isMobile]);

    return <div id="globe" data-tour="globe" style={globeStyles} />;
};

export default memo(Globe);
